import { useEffect, useState } from "react";
import { X, Check, ShoppingCartSimple, CaretDown, Sun } from "@phosphor-icons/react";

// Large, one-handed in-store view of a shopping list, grouped by aisle.
function useWakeLock() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!("wakeLock" in navigator)) return undefined;
    let lock = null;
    let dead = false;
    const grab = async () => {
      try {
        lock = await navigator.wakeLock.request("screen");
        if (dead) { lock.release(); return; }
        setOn(true);
        lock.addEventListener("release", () => setOn(false));
      } catch { setOn(false); }
    };
    const onVis = () => { if (!document.hidden) grab(); };
    grab();
    document.addEventListener("visibilitychange", onVis);
    return () => { dead = true; document.removeEventListener("visibilitychange", onVis); lock?.release().catch(() => {}); };
  }, []);
  return on;
}

const qtyText = (it) => (it.quantity ? `${+Number(it.quantity).toFixed(2)} ${it.unit || ""}`.trim() : "");

const Row = ({ it, onToggle }) => (
  <li>
    <button
      type="button"
      onClick={() => onToggle(it)}
      data-testid={`shopmode-item-${it.item_id}`}
      aria-pressed={!!it.checked}
      className={`w-full min-h-[68px] flex items-center gap-4 px-4 py-3 text-left rounded-2xl transition-[background-color,transform] duration-150 active:scale-[0.98] ${it.checked ? "bg-white/5 text-white/45" : "bg-white/10 hover:bg-white/15 text-white"}`}
    >
      <span className={`w-10 h-10 shrink-0 rounded-full border-2 flex items-center justify-center ${it.checked ? "bg-rf-orange border-rf-orange" : "border-white/60"}`}>
        {it.checked && <Check size={22} weight="bold" className="text-white" />}
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-xl font-bold leading-tight ${it.checked ? "line-through" : ""}`}>{it.display_name}</span>
        {qtyText(it) && <span className="block text-base text-white/60 mt-0.5">{qtyText(it)}</span>}
      </span>
    </button>
  </li>
);

const Aisle = ({ aisle, rows, onToggle }) => (
  <section className="mb-6" data-testid={`shopmode-aisle-${aisle.key}`}>
    <h2 className="sticky top-0 z-10 bg-[#1c1712]/95 backdrop-blur-md py-2 text-sm font-black uppercase tracking-[0.18em] text-rf-orange flex justify-between">
      <span><span aria-hidden="true">{aisle.emoji}</span> {aisle.label}</span>
      <span className="text-white/50">{rows.length}</span>
    </h2>
    <ul className="space-y-2 mt-1">{rows.map((it) => <Row key={it.item_id} it={it} onToggle={onToggle} />)}</ul>
  </section>
);

const Trolley = ({ rows, onToggle }) => {
  const [open, setOpen] = useState(true);
  if (!rows.length) return null;
  return (
    <section className="mt-8 border-t border-white/10 pt-4" data-testid="shopmode-trolley">
      <button type="button" onClick={() => setOpen(!open)} data-testid="shopmode-trolley-toggle"
              className="w-full flex items-center justify-between py-2 text-sm font-black uppercase tracking-[0.18em] text-white/60">
        <span className="inline-flex items-center gap-2"><ShoppingCartSimple size={18} weight="bold" /> In trolley · {rows.length}</span>
        <CaretDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <ul className="space-y-2 mt-1">{rows.map((it) => <Row key={it.item_id} it={it} onToggle={onToggle} />)}</ul>}
    </section>
  );
};

export const ShoppingMode = ({ items, aisles, listName, live, onToggle, onClose }) => {
  const awake = useWakeLock();
  const todo = items.filter((i) => !i.checked);
  const done = items.filter((i) => i.checked);
  const pct = items.length ? Math.round((done.length / items.length) * 100) : 0;
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[90] bg-[#1c1712] text-white flex flex-col" role="dialog" aria-label="Shopping mode" data-testid="shopping-mode">
      <header className="px-5 pt-5 pb-3 border-b border-white/10">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-rf-orange">Shopping mode</p>
        <div className="flex items-end justify-between gap-3 mt-1">
          <h1 className="font-display font-black text-3xl leading-none truncate" data-testid="shopmode-title">{listName}</h1>
          <p className="text-lg font-bold text-white/80 shrink-0" data-testid="shopmode-progress">{done.length}/{items.length}</p>
        </div>
        <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-rf-orange transition-[width] duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 flex gap-4 text-xs text-white/50">
          {awake && <span className="inline-flex items-center gap-1" data-testid="shopmode-awake"><Sun size={14} /> Screen stays on</span>}
          {live && <span className="inline-flex items-center gap-1.5 text-emerald-400" data-testid="shopmode-live"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live with others</span>}
        </div>
      </header>
      <main className="flex-1 overflow-y-auto px-4 pb-32 pt-2">
        {!todo.length && (
          <div className="text-center py-16" data-testid="shopmode-done">
            <Check size={64} weight="bold" className="mx-auto text-rf-orange" />
            <p className="font-display font-black text-3xl mt-4">{items.length ? "All in the trolley!" : "Nothing on this list"}</p>
          </div>
        )}
        {aisles.map((a) => {
          const rows = todo.filter((i) => (i.aisle || "other") === a.key);
          return rows.length ? <Aisle key={a.key} aisle={a} rows={rows} onToggle={onToggle} /> : null;
        })}
        <Trolley rows={done} onToggle={onToggle} />
      </main>
      <footer className="fixed bottom-0 inset-x-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-[#1c1712] via-[#1c1712]/95 to-transparent">
        <button type="button" onClick={onClose} data-testid="shopmode-exit"
                className="w-full h-16 rounded-full bg-white text-rf-ink text-lg font-black inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <X size={22} weight="bold" /> Exit shopping mode
        </button>
      </footer>
    </div>
  );
};
